import { useEffect } from 'react';

interface SeoOptions {
    title: string;
    description: string;
    url?: string;
    type?: 'website' | 'article';
    publishedTime?: string;
    image?: string;
    imageWidth?: number;
    imageHeight?: number;
}

const DEFAULT_TITLE = 'Schedi - program do grafików pracy dla gastronomii';
const DEFAULT_DESC  = 'Schedi to program do grafików pracy dla restauracji, kawiarni i barów. Twórz grafiki, zbieraj dostępność i komunikuj się z zespołem.';
const BASE_URL      = 'https://www.schedi.pl';
const DEFAULT_IMAGE = '/og-schedi.png';
const DEFAULT_IMAGE_WIDTH = 1200;
const DEFAULT_IMAGE_HEIGHT = 630;

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
    let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
}

function setCanonical(href: string) {
    let el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
    }
    el.setAttribute('href', href);
}

function toAbsoluteUrl(pathOrUrl: string) {
    return pathOrUrl.startsWith('http') ? pathOrUrl : `${BASE_URL}${pathOrUrl}`;
}

export function useSeo({
    title,
    description,
    url,
    type = 'website',
    publishedTime,
    image = DEFAULT_IMAGE,
    imageWidth = DEFAULT_IMAGE_WIDTH,
    imageHeight = DEFAULT_IMAGE_HEIGHT,
}: SeoOptions) {
    useEffect(() => {
        const fullTitle = `${title} | Schedi`;
        const fullUrl   = url ? `${BASE_URL}${url}` : BASE_URL;
        const fullImage = toAbsoluteUrl(image);

        document.title = fullTitle;
        setCanonical(fullUrl);

        setMeta('description', description);

        setMeta('og:title',       fullTitle,   'property');
        setMeta('og:description', description, 'property');
        setMeta('og:url',         fullUrl,     'property');
        setMeta('og:type',        type,        'property');
        setMeta('og:site_name',   'Schedi',    'property');
        setMeta('og:image',       fullImage,   'property');
        setMeta('og:image:width',  String(imageWidth),  'property');
        setMeta('og:image:height', String(imageHeight), 'property');

        setMeta('twitter:card',        'summary_large_image');
        setMeta('twitter:title',       fullTitle);
        setMeta('twitter:description', description);
        setMeta('twitter:image',       fullImage);

        if (type === 'article' && publishedTime) {
            setMeta('article:published_time', publishedTime, 'property');
        }

        return () => {
            document.title = DEFAULT_TITLE;
            setCanonical(BASE_URL);
            setMeta('description', DEFAULT_DESC);
        };
    }, [title, description, url, type, publishedTime, image, imageWidth, imageHeight]);
}
